/**
 * Crescent — Products listing (card grid).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import CrescentLayout from "./CrescentLayout";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CrescentProductsIndex() {
  const [items, setItems] = useState(null);

  usePageSeo({
    title: "Flexible Packaging Products — Crescent Flexi Pack",
    description: "Explore Crescent's full flexible packaging catalogue: stand-up pouches, zipper pouches, center-seal, side-seal, gusseted pouches, printed laminated rolls, LDPE/LLDPE films and recyclable mono-material bags.",
  });
  useBreadcrumbSchema([
    { name: "Home", url: "/crescent" },
    { name: "Products", url: "/crescent/products" },
  ]);

  useEffect(() => {
    axios.get(`${API}/crescent/products`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  return (
    <CrescentLayout>
      <div data-testid="crescent-products-index">
        <section className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-xs text-amber-100 mb-3">
              <Link to="/crescent" className="hover:underline">Home</Link>
              <span className="mx-2">›</span>
              <span className="text-white">Products</span>
            </nav>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-tight">Our Products</h1>
            <p className="mt-3 text-white/90 max-w-2xl text-lg">
              Eight product families — all engineered to your product, shelf-life and distribution profile.
              Material structure and barrier are confirmed in writing before every production run.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {items === null && <div className="text-center py-10 text-slate-400"><Loader2 className="mx-auto animate-spin" size={24} /></div>}
            {items && items.length === 0 && <div className="text-center text-slate-500">No products yet.</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="products-list">
              {(items || []).map((p) => (
                <Link
                  key={p.slug}
                  to={`/crescent/products/${p.slug}`}
                  data-testid={`product-card-${p.slug}`}
                  className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-emerald-500 hover:shadow-xl transition-all flex flex-col"
                >
                  <div className={`h-48 bg-slate-100 bg-cover bg-center bg-gradient-to-br ${p.fallback_gradient || 'from-emerald-600 to-slate-800'}`}
                    style={{ backgroundImage: `url(${p.home_card_image || p.image})` }} />
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-heading font-bold text-slate-900 text-lg group-hover:text-emerald-700">{p.title}</h3>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed line-clamp-3">{p.lede}</p>
                    <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 pt-4">
                      Explore details <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </CrescentLayout>
  );
}
